import { useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
  LayoutGrid, Mail, Users, TrendingUp,
  Settings, ChevronsLeft,
  ChevronsRight, Bell, LogOut, ChevronDown,
  Receipt, Inbox, Activity, Flame, Search,
} from 'lucide-react';
import { Logo } from './Logo.jsx';
import { NotificationBell } from './NotificationBell.jsx';
import { AmbstatNavGroup } from './ambstat/NavGroup.jsx';

import { getInitials } from '../utils.js';
import { useCurrentUser } from '../hooks/useCurrentUser';

const STORAGE_KEY = 'sidebar-collapsed';

const NAV_MAIN = [
  { id: 'kanban',      label: 'Доска',       path: '/',            icon: LayoutGrid },
  { id: 'outreach',    label: 'Аутрич',      path: '/outreach',    icon: Mail },
  { id: 'inbox',       label: 'Входящие',    path: '/inbox',       icon: Inbox },
  { id: 'bloggers',    label: 'Блогеры',     path: '/bloggers',    icon: Users },
  { id: 'finder',      label: 'Поиск блогеров', path: '/finder',   icon: Search },
  // HIDDEN 2026-09-24 (owner, M2-HIDE-OLD): the old rounds «Мониторинг» — /monitoring redirects to /monitoring2 in App.jsx;
  // MonitoringView, its components, backend routes and data stay for reuse.
  // { id: 'monitoring',  label: 'Мониторинг',  path: '/monitoring',  icon: Activity },     // Phase 83 — D-83-05
  { id: 'monitoring2', label: 'Мониторинг', path: '/monitoring2', icon: Activity },
  { id: 'ambstat',     label: 'Амбстат',     path: '/ambstat',     icon: Flame },        // SCOPE-HOURLY-METRICS H2b — часовые метрики амбассадоров
  { id: 'stats',       label: 'Статистика',  path: '/stats',       icon: TrendingUp },
  { id: 'documents',   label: 'Документы',   path: '/documents',   icon: Users },
];

const NAV_INVOICES = { id: 'invoices', label: 'Инвойсы', path: '/invoices', icon: Receipt };
const NAV_TEAM = { id: 'team', label: 'Команда', path: '/team', icon: Users };

function MonitoringNavGroup ({ collapsed, active }) {
  const location = useLocation(); const navigate = useNavigate();
  const [open, setOpen] = useState(active); const [top, setTop] = useState(null);
  const icon = useRef(null); const flyout = useRef(null);
  useEffect(() => setOpen(active), [active]);
  useEffect(() => {
    if (top == null || !collapsed) return undefined;
    flyout.current?.querySelector('[aria-current="page"], button')?.focus();
    const close = () => { setTop(null); icon.current?.focus(); };
    const down = (e) => { if (!flyout.current?.contains(e.target) && !icon.current?.contains(e.target)) close(); };
    const key = (e) => { const past = e.key === 'Tab' && !e.shiftKey && e.target === flyout.current?.querySelector('li:last-child > button'); if (past) e.preventDefault(); if (e.key === 'Escape' || past) close(); };
    document.addEventListener('mousedown', down); document.addEventListener('keydown', key);
    return () => { document.removeEventListener('mousedown', down); document.removeEventListener('keydown', key); };
  }, [top, collapsed]);
  const { isAccountant } = useCurrentUser();
  const go = (path) => { setTop(null); if (path.split('?')[0] !== location.pathname) navigate(path); };
  const src = new URLSearchParams(location.search);
  const geoPath = `/monitoring2/geo${src.get('startDate') && src.get('endDate') ? `?${new URLSearchParams({ startDate: src.get('startDate'), endDate: src.get('endDate') })}` : ''}`;
  const sections = [['/monitoring2', 'Таблицы'], ['/monitoring2/reports', 'Отчёты'], ...(!isAccountant ? [['/monitoring2/geo', 'Гео']] : [])];
  const items = <ul className="amb-nav__list">{sections.map(([path, title]) => <li key={path}><button type="button" className="amb-nav__item" aria-current={active && (path === '/monitoring2' ? location.pathname === path : location.pathname.startsWith(path)) ? 'page' : undefined} onClick={() => go(path === '/monitoring2/geo' ? geoPath : path)}>{title}</button></li>)}</ul>;
  if (collapsed) return <><button ref={icon} type="button" className={`sidebar__item${active ? ' sidebar__item--active' : ''}`} title="Мониторинг" aria-expanded={top != null} onClick={(e) => setTop(top == null ? e.currentTarget.getBoundingClientRect().top : null)}><Activity size={18} strokeWidth={1.6} /></button>
    {top != null && createPortal(<nav ref={flyout} className="amb-nav__flyout" aria-label="Разделы мониторинга" style={{ top }} onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setTop(null); }}>{items}</nav>, document.body)}</>;
  return <><div className="amb-nav__row"><button type="button" className={`sidebar__item${active ? ' sidebar__item--active' : ''}`} onClick={() => go('/monitoring2')}><Activity size={18} strokeWidth={1.6} /><span>Мониторинг</span></button><button type="button" className={`amb-nav__chev${open ? ' amb-nav__chev--open' : ''}`} aria-expanded={open} aria-controls="monitoring-nav-list" aria-label="Разделы мониторинга" onClick={() => setOpen((v) => !v)}><ChevronDown size={14} /></button></div>{open && <div id="monitoring-nav-list">{items}</div>}</>;
}

export function Sidebar({
  user, isAdmin, adminUsers = [],
  onLogout, onImpersonate, onOpenLead,
  impersonatedBy, onExitImpersonation,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAccountant } = useCurrentUser();

  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === 'true'; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, String(collapsed)); }
    catch { /* ignore */ }
  }, [collapsed]);

  const activeView = (() => {
    const p = location.pathname;
    if (p.startsWith('/inbox')) return 'inbox';
    if (p.startsWith('/bloggers')) return 'bloggers';
    if (p.startsWith('/finder')) return 'finder';
    if (p === '/monitoring2' || p.startsWith('/monitoring2/')) return 'monitoring2';
    if (p.startsWith('/monitoring')) return 'monitoring';
    if (p.startsWith('/ambstat')) return 'ambstat';
    if (p.startsWith('/ambassadors')) return 'ambassadors';
    if (p.startsWith('/outreach')) return 'outreach';
    if (p.startsWith('/stats')) return 'stats';
    if (p.startsWith('/team')) return 'team';
    if (p.startsWith('/documents')) return 'documents';
    if (p.startsWith('/invoices')) return 'invoices';
    if (p.startsWith('/settings')) return 'settings';
    return 'kanban';
  })();

  const renderItem = ({ id, label, path, icon: Icon }) => (
    <button
      key={id}
      type="button"
      className={`sidebar__item${activeView === id ? ' sidebar__item--active' : ''}`}
      onClick={() => navigate(path)}
      title={collapsed ? label : undefined}
    >
      <Icon size={18} strokeWidth={1.6} />
      {!collapsed && <span>{label}</span>}
    </button>
  );

  return (
    <aside className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}`}>
      {/* ── Logo + Toggle ── */}
      <div className="sidebar__head">
        <div
          className="sidebar__logo"
          onClick={() => navigate('/')}
          style={{ cursor: 'pointer' }}
          title="На главную"
        >
          <Logo compact={collapsed} />
        </div>
        <button
          type="button"
          className="sidebar__toggle"
          onClick={() => setCollapsed(c => !c)}
          title={collapsed ? 'Развернуть' : 'Свернуть'}
        >
          {collapsed ? <ChevronsRight size={16} /> : <ChevronsLeft size={16} />}
        </button>
      </div>

      {/* ── Main Nav ── */}
      {/*
        «Инвойсы» is role-guarded (ADMIN / TEAM_LEAD / ACCOUNTANT / MANAGER) and MUST appear LAST,
        strictly after «Амбассадоры» — CONTEXT.md Navigation lock.
        Phase 66.1 D-09: MANAGER added — read-only access to own invoices via per-row scope on backend.
        ACCOUNTANT continues to see only «Документы» + «Инвойсы»; MANAGER sees full NAV_MAIN + Инвойсы.
      */}
      <nav className="sidebar__nav">
        {(isAccountant ? NAV_MAIN.filter(item => item.id === 'documents') : NAV_MAIN.filter(item => (user?.role !== 'MANAGER' || !['outreach', 'stats', 'ambstat'].includes(item.id)) && (item.id !== 'inbox' || user?.messagingEnabled === true))).map((it) => (it.id === 'ambstat'
          ? <AmbstatNavGroup key={it.id} item={it} collapsed={collapsed} active={activeView === 'ambstat'} /> : it.id === 'monitoring2' ? <MonitoringNavGroup key={it.id} collapsed={collapsed} active={activeView === 'monitoring2'} /> : renderItem(it)))}
        {(user?.role === 'ADMIN' || user?.role === 'TEAM_LEAD' || user?.role === 'ASSISTANT') && renderItem(NAV_TEAM)}
        {(user?.role === 'ADMIN' || user?.role === 'TEAM_LEAD' || user?.role === 'ACCOUNTANT' || user?.role === 'MANAGER' || user?.role === 'ASSISTANT') && renderItem(NAV_INVOICES)}
      </nav>

      {/* ── Footer ── */}
      <div className="sidebar__footer">
        {/* Notifications — button fills the row so entire item opens the panel */}
        <div className="sidebar__item sidebar__item--notif">
          <NotificationBell onOpenLead={onOpenLead} label={collapsed ? undefined : 'Уведомления'} />
        </div>

        {/* POST-W1 T3 (2026-08-27, owner) — «Reply.io mapping» moved out of the main menu
            into «Настройки» as a tab (SettingsPanel.jsx, gate unchanged: ADMIN / TEAM_LEAD). */}

        {/* Settings */}
        <button
          type="button"
          className={`sidebar__item${activeView === 'settings' ? ' sidebar__item--active' : ''}`}
          onClick={() => navigate('/settings')}
          title={collapsed ? 'Настройки' : undefined}
        >
          <Settings size={18} strokeWidth={1.6} />
          {!collapsed && <span>Настройки</span>}
        </button>

        {/* User */}
        {user && (
          <div className="sidebar__user">
            <div className="sidebar__user-avatar">{getInitials(user.email)}</div>
            {!collapsed && (
              <div className="sidebar__user-info">
                <span className="sidebar__user-name">{user.name || user.email}</span>
                <span className="sidebar__user-role">
                  {user.role}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Actions (expanded only) */}
        {!collapsed && user && (
          <div className="sidebar__actions">
            <button className="sidebar__action-btn" onClick={onLogout}>
              <LogOut size={14} />
              <span>Выйти</span>
            </button>
            {isAdmin && adminUsers.length > 0 && (
              <select
                className="sidebar__impersonate"
                value=""
                onChange={(e) => onImpersonate(e.target.value)}
              >
                <option value="">Войти как...</option>
                {adminUsers.filter(u => u.id !== user?.id && u.isActive).map(u => (
                  <option key={u.id} value={u.id}>{u.name || u.email}</option>
                ))}
              </select>
            )}
          </div>
        )}
      </div>
    </aside>
  );
}
